import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/contexts/auth-context";
import { usePlayerCredits } from "@/hooks/use-coach";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Link } from "wouter";
import {
  Coins,
  Zap,
  Gem,
  Calendar,
  Copy,
  Share2,
  Users,
  Target,
  Gift,
  Star,
  Crown,
  HelpCircle,
  Building2
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { trackEvent } from "@/lib/analytics/posthog";

interface ReferralStats {
  referralCode: string | null;
  totalReferrals: number;
  bonusCredits: number;
  referrals: Array<{
    id: number;
    referredUserId: string;
    bonusCreditsAwarded: number;
    referredAt: string;
  }>;
  nextMilestone: number | null;
  creditsAtNextMilestone: number | null;
}

export default function MyCreditsPage() {
  const { isAuthenticated, isLoading, user } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [referralCode, setReferralCode] = useState("");

  // Query to fetch user's credits
  const { data: creditsData, isLoading: creditsLoading, error: creditsError } = useQuery({
    queryKey: ['/api/users', user?.id, 'credits'],
    enabled: !!user?.id,
    retry: false,
  });

  // Consultation credits moved to dedicated page

  // Query to fetch user's referral stats
  const { data: referralStats, isLoading: referralStatsLoading, error: referralStatsError } = useQuery<ReferralStats>({
    queryKey: ['/api/users', user?.id, 'referral-stats'],
    enabled: !!user?.id,
    retry: false,
  });

  // Query to fetch player's team credits
  const { data: playerCredits } = usePlayerCredits();

  // Mutation to apply referral code
  const applyReferralMutation = useMutation({
    mutationFn: async (data: { referrerCode: string; referredUserId: string }) => {
      const response = await apiRequest('POST', '/api/referrals', data);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to apply referral code');
      }
      return response.json();
    },
    onSuccess: (data: any) => {
      toast({
        title: "Referral Applied!",
        description: `You received +${data?.referredCreditsAwarded ?? 1} credit. ${data.creditsAwarded > 0 ? `The referrer earned ${data.creditsAwarded} bonus credits.` : ''}`,
      });
      try {
        trackEvent('referral_code_applied', {
          referrer_code: referralCode.trim(),
          credits_awarded: data?.creditsAwarded ?? 0,
          referred_credits_awarded: data?.referredCreditsAwarded ?? 1,
        });
      } catch {}
      // Clear the referral code input
      setReferralCode('');
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['/api/users', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['/api/users', user?.id, 'credits'] });
      queryClient.invalidateQueries({ queryKey: ['/api/users', user?.id, 'referral-stats'] });
    },
    onError: (error: any) => {
      toast({
        title: "Referral Failed",
        description: error.message || "Unable to apply referral code",
        variant: "destructive",
      });
    }
  });

  useEffect(() => {
    // Redirect to login if not authenticated
    if (!isLoading && !isAuthenticated) {
      setLocation('/login');
      return;
    }
  }, [isAuthenticated, isLoading, setLocation]);

  // Handle user data errors (user doesn't exist in database)
  useEffect(() => {
    if (creditsError || referralStatsError) {
      toast({
        title: "Session Error",
        description: "Your session has expired. Please log in again.",
        variant: "destructive",
      });
      setLocation('/login');
    }
  }, [creditsError, referralStatsError, toast, setLocation]);

  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;
    if (creditsLoading || referralStatsLoading) return;
    try {
      const creditsValue = (creditsData as any)?.credits || 0;
      const bonus = (referralStats as any)?.bonusCredits || 0;
      const totalReferrals = (referralStats as any)?.totalReferrals || 0;
      trackEvent('credits_viewed', {
        credits: creditsValue,
        bonus_credits: bonus,
        total_referrals: totalReferrals,
      });
    } catch {}
  }, [isAuthenticated, user?.id, creditsLoading, referralStatsLoading, creditsData, referralStats]);

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen py-20 bg-gradient-to-br from-[var(--light-1)] to-[var(--light-2)] dark:bg-gradient-to-br dark:from-[var(--dark-1)] dark:to-[var(--dark-2)] flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-gray-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">Loading...</p>
        </div>
      </div>
    );
  }

  // Don't render anything if not authenticated (redirect is handled in useEffect)
  if (!isAuthenticated) {
    return null;
  }

  const credits = (creditsData as any)?.credits || 0;
  const nextResetAt = (creditsData as any)?.nextResetAt || null;
  const nextResetFormatted = nextResetAt ? new Date(nextResetAt).toLocaleDateString() : null;
  const planCredits = (creditsData as any)?.planCredits || 0;
  const bonusCredits = (creditsData as any)?.bonusCredits || 0;


  const copyReferralCode = () => {
    if (referralStats?.referralCode) {
      navigator.clipboard.writeText(referralStats.referralCode);
      toast({
        title: "Copied!",
        description: "Referral code copied to clipboard",
      });
      try {
        trackEvent('referral_code_copied', {
          total_referrals: referralStats.totalReferrals,
        });
      } catch {}
    }
  };

  const shareReferralCode = () => {
    if (referralStats?.referralCode && navigator.share) {
      navigator.share({
        title: 'Join Bounce Bunny with my referral code!',
        text: `Use my referral code "${referralStats.referralCode}" to start your dunking training journey!`,
        url: window.location.origin,
      });
      try {
        trackEvent('referral_share_triggered', {
          total_referrals: referralStats.totalReferrals,
          used_navigator_share: true,
        });
      } catch {}
    } else {
      copyReferralCode();
    }
  };

  const handleApplyReferral = () => {
    if (!referralCode.trim()) {
      toast({
        title: "Invalid Code",
        description: "Please enter a referral code",
        variant: "destructive",
      });
      return;
    }

    if (!user?.id) {
      toast({
        title: "Error",
        description: "Please log in again to apply referral code",
        variant: "destructive",
      });
      return;
    }

    // Check if user is already referred
    if ((user as any).referredBy) {
      toast({
        title: "Already Referred",
        description: "You have already been referred by someone",
        variant: "destructive",
      });
      return;
    }

    applyReferralMutation.mutate({
      referrerCode: referralCode.trim(),
      referredUserId: user.id.toString(),
    });
  };

  const getMilestoneProgress = () => {
    if (!referralStats) return { progress: 0, description: "Loading..." };
    
    const { totalReferrals, nextMilestone } = referralStats;
    
    if (nextMilestone === null) {
      return { progress: 100, description: "All milestones completed! 🎉" };
    }
    
    const progress = (totalReferrals / nextMilestone) * 100;
    const remaining = nextMilestone - totalReferrals;
    
    return {
      progress,
      description: `${remaining} more referral${remaining !== 1 ? 's' : ''} to earn ${referralStats.creditsAtNextMilestone} credits`
    };
  };

  const milestoneProgress = getMilestoneProgress();

  return (
    <section className="min-h-screen py-20 bg-gradient-to-br from-[var(--light-1)] to-[var(--light-2)] dark:bg-gradient-to-br dark:from-[var(--dark-1)] dark:to-[var(--dark-2)]">
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">My Credits & Referrals</h2>
            <p className="text-gray-600 dark:text-gray-300 text-lg">
              Track your credits and earn more through referrals
            </p>
          </div>

          {(creditsLoading || referralStatsLoading) ? (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-4 border-gray-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600 dark:text-gray-300">Loading your credits...</p>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Team Credits Card - Only shown if player is on a team */}
              {playerCredits?.team && playerCredits.team.canUseTeamCredits && (
                <Card className="bg-gradient-to-br from-[var(--apple-blue)] to-[var(--apple-purple)] rounded-3xl shadow-lg p-8 text-white">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur">
                        <Building2 className="w-8 h-8 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold opacity-90">Team Credits</h3>
                        <p className="text-sm opacity-75">{playerCredits.team.teamName}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-4xl font-bold">{playerCredits.team.teamCredits ?? 0}</p>
                      <p className="text-sm opacity-75">available</p>
                    </div>
                  </div>
                  <div className="mt-4 bg-white/10 rounded-xl p-4 backdrop-blur">
                    <p className="text-sm opacity-90">
                      <Users className="inline w-4 h-4 mr-2" />
                      Team credits are used first when generating training plans. When exhausted, your personal credits are used.
                    </p>
                  </div>
                </Card>
              )}

              {/* Credits Overview Card */}
              <Card className="glass-card dark:glass-card-dark rounded-3xl shadow-lg card-hover p-8">
                <div className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-emerald-400 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-emerald-500/30 ring-2 ring-emerald-300/40">
                    <Gem className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-4xl font-bold mb-2 text-white">
                    {credits}
                  </h3>

                  <div className="flex items-center justify-center gap-2 mb-2 translate-x-3">
                     <p className="text-xl text-gray-600 dark:text-gray-300">Total Credits</p>
                     <Popover>
                       <PopoverTrigger asChild>
                         <button
                           type="button"
                           aria-label="Total credits info"

                          className="text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-200"
                         >
                           <HelpCircle className="h-4 w-4" />
                         </button>
                       </PopoverTrigger>
                       <PopoverContent side="right" className="w-auto whitespace-nowrap rounded-2xl border border-gray-900/70 bg-gray-900 text-white text-sm shadow-2xl ring-1 ring-black/20 px-4 py-3">
                         Each training plan generation uses 1 credit.
                       </PopoverContent>
                     </Popover>
                   </div>
                  {referralStats && referralStats.bonusCredits > 0 && (
                    <p className="text-sm text-[var(--apple-green)] mb-6">
                      <Gift className="inline w-4 h-4 mr-1" />
                      {referralStats.bonusCredits} bonus credits from referrals
                    </p>
                  )}
                  

                  {credits > 0 ? (
                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-2xl p-4 mb-6">
                      <p className="text-green-700 dark:text-green-300 font-medium">
                        You can generate {credits} more training plan{credits !== 1 ? 's' : ''}
                      </p>
                      {nextResetFormatted && (
                        <p className="text-sm text-green-700/80 dark:text-green-300/80 mt-2">
                          Next reset date: {nextResetFormatted}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-2xl p-4 mb-6">
                      <p className="text-orange-700 dark:text-orange-300 font-medium">
                        You've used all your credits. Refer friends to earn more!
                      </p>
                      {nextResetFormatted && (
                        <p className="text-sm text-orange-700/80 dark:text-orange-300/80 mt-2">
                          Next reset date: {nextResetFormatted}
                        </p>
                      )}
                    </div>
                  )}

                  <div className="flex justify-center gap-4">
                    <Link href="/form">
                      <Button 
                        variant="outline"
                        className="px-8 py-3 rounded-2xl"
                        disabled={credits <= 0}
                      >
                        <Zap className="mr-2 h-4 w-4" />
                        Generate New Plan
                      </Button>
                    </Link>
                    <Link href="/my-plans">
                      <Button variant="outline" className="px-8 py-3 rounded-2xl">
                        <Calendar className="mr-2 h-4 w-4" />
                        View My Plans
                      </Button>
                    </Link>
                  </div>
                </div>
              </Card>

              {/* Referral System */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Share Your Code */}
                <Card className="glass-card dark:glass-card-dark rounded-3xl shadow-lg p-8">
                  <CardHeader className="p-0 mb-6">
                    <CardTitle className="text-2xl font-bold flex items-center">
                      <Share2 className="mr-3 h-6 w-6 text-[var(--apple-green)]" />
                      Share Your Code
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    {referralStats && (
                      <>
                        <div className="bg-gray-50 dark:bg-[var(--dark-3)] rounded-2xl p-4 mb-4">
                          <div className="flex items-center justify-between">
                            <code className="text-lg font-mono font-bold text-gray-700 dark:text-gray-200">
                              {referralStats.referralCode}
                            </code>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={copyReferralCode}
                                className="rounded-xl"
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                onClick={shareReferralCode}
                                className="btn-primary rounded-xl"
                              >
                                <Share2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                        
                        <div className="text-center mb-6">
                          <div className="flex items-center justify-center gap-4 mb-4">
                            <Users className="h-6 w-6 text-gray-600 dark:text-gray-300" />
                            <span className="text-2xl font-bold">{referralStats.totalReferrals}</span>
                            <span className="text-gray-600 dark:text-gray-300">referrals</span>
                          </div>
                          
                          {referralStats.nextMilestone && (
                            <div className="space-y-2">
                              <div className="bg-gray-200 dark:bg-[var(--dark-3)] rounded-full h-2">
                                <div 
                                  className="bg-gradient-to-r from-[var(--apple-orange)] to-[var(--apple-orange-light)] h-2 rounded-full transition-all duration-300"
                                  style={{ width: `${milestoneProgress.progress}%` }}
                                />
                              </div>
                              <p className="text-sm text-gray-600 dark:text-gray-300">
                                {milestoneProgress.description}
                              </p>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>

                {/* Apply Referral Code */}
                <Card className="glass-card dark:glass-card-dark rounded-3xl shadow-lg p-8">
                  <CardHeader className="p-0 mb-6">
                    <CardTitle className="text-2xl font-bold flex items-center">
                      <Gift className="mr-3 h-6 w-6 text-[var(--apple-green)]" />
                      Have a Referral Code?
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="space-y-4">
                      <Input
                        placeholder="Enter referral code"
                        value={referralCode}
                        onChange={(e) => setReferralCode(e.target.value)}
                        className="rounded-2xl"
                      />
                      <Button
                        onClick={handleApplyReferral}
                        disabled={applyReferralMutation.isPending || !referralCode.trim()}
                        className="w-full btn-primary rounded-2xl"
                      >
                        {applyReferralMutation.isPending ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        ) : (
                          <Target className="mr-2 h-4 w-4" />
                        )}
                        Apply Referral Code
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Referral Rewards */}
              <Card className="glass-card dark:glass-card-dark rounded-3xl shadow-lg p-8">
                <h3 className="text-2xl font-bold mb-6 flex items-center">
                  <Crown className="mr-3 h-6 w-6 text-[var(--apple-orange)]" />
                  Referral Rewards
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Milestone 1 - 2 friends */}
                  <div
                    className={`relative group text-center p-6 rounded-2xl shadow-lg transition-all duration-300 will-change-transform 
                    ${(referralStats?.totalReferrals ?? 0) >= 2
                      ? 'bg-gradient-to-br from-orange-500 to-amber-600 text-white ring-2 ring-black/30 dark:ring-amber-300/50 shadow-lg shadow-orange-500/30 hover:shadow-orange-400/40 rotate-[-2deg] hover:rotate-0 hover:-translate-y-1'
                      : 'bg-black text-gray-300 border-2 border-orange-500/40 grayscale hover:grayscale-0 hover:border-orange-500 hover:shadow-orange-300/20 rotate-[-2deg] hover:rotate-0 hover:-translate-y-1'}`}
                  >
                    {/* glossy highlight */}
                    <div className="pointer-events-none absolute inset-0 rounded-2xl overflow-hidden">
                      <div className="absolute top-0 left-0 right-0 h-1/3 bg-white/40 dark:bg-white/10" />
                    </div>
                  
                    {/* ribbon chip */}
                    <div className="absolute -top-2 right-3 rotate-6">
                      <div className={`${(referralStats?.totalReferrals ?? 0) >= 2 ? 'bg-black text-orange-400' : 'bg-transparent text-orange-500 border border-orange-500'} px-3 py-1 rounded-full text-lg font-bold shadow-sm`}>+3</div>
                    </div>
                  
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 
                      ${(referralStats?.totalReferrals ?? 0) >= 2 ? 'bg-black text-orange-400' : 'border-2 border-orange-500 text-orange-500 bg-transparent'}`}>
                      <Users className="w-6 h-6" aria-label="Two friends milestone" />
                    </div>
                    <h4 className="font-semibold mb-1">First Milestone</h4>
                    <p className="text-[11px] tracking-wider uppercase text-gray-600 dark:text-gray-300 mb-1">Refer 2 friends to earn 3 credits</p>
                  </div>
                  
                  {/* Milestone 2 - 5 friends */}
                  <div
                    className={`relative group text-center p-6 rounded-2xl shadow-lg transition-all duration-300 will-change-transform 
                    ${(referralStats?.totalReferrals ?? 0) >= 5
                      ? 'bg-gradient-to-br from-orange-500 to-amber-600 text-white ring-2 ring-black/30 dark:ring-amber-300/50 shadow-lg shadow-orange-500/30 hover:shadow-orange-400/40 rotate-[1deg] hover:rotate-0 hover:-translate-y-1'
                      : 'bg-black text-gray-300 border-2 border-orange-500/40 grayscale hover:grayscale-0 hover:border-orange-500 hover:shadow-orange-300/20 rotate-[1deg] hover:rotate-0 hover:-translate-y-1'}`}
                  >
                    {/* glossy highlight */}
                    <div className="pointer-events-none absolute inset-0 rounded-2xl overflow-hidden">
                      <div className="absolute top-0 left-0 right-0 h-1/3 bg-white/40 dark:bg-white/10" />
                    </div>
                  
                    {/* ribbon chip */}
                    <div className="absolute -top-2 right-3 -rotate-3">
                      <div className={`${(referralStats?.totalReferrals ?? 0) >= 5 ? 'bg-black text-orange-400' : 'bg-transparent text-orange-500 border border-orange-500'} px-3 py-1 rounded-full text-base font-bold shadow-sm`}>+10</div>
                    </div>
                  
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 
                      ${(referralStats?.totalReferrals ?? 0) >= 5 ? 'bg-black text-orange-400' : 'border-2 border-orange-500 text-orange-500 bg-transparent'}`}>
                      <Gift className="w-6 h-6" aria-label="Five friends milestone" />
                    </div>
                    <h4 className="font-semibold mb-1">Growing Network</h4>
                    <p className="text-[11px] tracking-wider uppercase text-gray-600 dark:text-gray-300 mb-1">Refer 5 friends to earn 10 credits</p>
                  </div>
                  
                  {/* Milestone 3 - 8 friends */}
                  <div
                    className={`relative group text-center p-6 rounded-2xl shadow-lg transition-all duration-300 will-change-transform 
                    ${(referralStats?.totalReferrals ?? 0) >= 8
                      ? 'bg-gradient-to-br from-orange-500 to-amber-600 text-white ring-2 ring-black/30 dark:ring-amber-300/50 shadow-lg shadow-orange-500/30 hover:shadow-orange-400/40 rotate-[-1deg] hover:rotate-0 hover:-translate-y-1'
                      : 'bg-black text-gray-300 border-2 border-orange-500/40 grayscale hover:grayscale-0 hover:border-orange-500 hover:shadow-orange-300/20 rotate-[-1deg] hover:rotate-0 hover:-translate-y-1'}`}
                  >
                    {/* glossy highlight */}
                    <div className="pointer-events-none absolute inset-0 rounded-2xl overflow-hidden">
                      <div className="absolute top-0 left-0 right-0 h-1/3 bg-white/40 dark:bg-white/10" />
                    </div>
                  
                    {/* ribbon chip */}
                    <div className="absolute -top-2 right-3 rotate-2">
                      <div className={`${(referralStats?.totalReferrals ?? 0) >= 8 ? 'bg-black text-orange-400' : 'bg-transparent text-orange-500 border border-orange-500'} px-3 py-1 rounded-full text-lg font-bold shadow-sm`}>+18</div>
                    </div>
                  
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 
                      ${(referralStats?.totalReferrals ?? 0) >= 8 ? 'bg-black text-orange-400' : 'border-2 border-orange-500 text-orange-500 bg-transparent'}`}>
                      <Star className="w-6 h-6" />
                    </div>
                    <h4 className="font-semibold mb-1">Elite Referrer</h4>
                    <p className="text-[11px] tracking-wider uppercase text-gray-600 dark:text-gray-300 mb-1">Refer 8 friends to earn 18 credits</p>
                  </div>
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}